import React from 'react';
import { Alert, Container, Row, Col, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Registration, { RegistrationTypes } from '../Registration/Registration';
import RegionSummary from '../../components/RegionSummary/RegionSummary';
import PaymentSelectButton from '../../components/PaymentSelectButton/PaymentSelectButton';
import CreditCardForm from '../../components/CreditCardForm/CreditCardForm';
import CurrencyText from '../../components/CurrencyText/CurrencyText';
import Contact from '../../components/Contact/Contact';
import Success from '../Success/Success';
import WhatsNextPopover from '../../components/WhatsNextPopover/WhatsNextPopover';
import CalculatorLayout from '../../components/CalculatorLayout/CalculatorLayout';

import { isPaymentRegion } from '../../utils/helpers';
import {
  getRegionFromForm,
  isPledgeOnlyRegion,
  calculateRenterPayment
} from '../../utils/paymentHelpers';
import { handlePaymentSubmission } from '../../utils/paymentSubmission';

import styles from './ImARenter.module.css';

const today = moment();
const currentYear = today.year();

class ImARenter extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: "register",
      registration: null,
      region: null,
      term: "monthly",
      monthlyPayment: 15,
      address: '',
      publicMap: true,
      pledgeOnly: true,
      total: 0,
      currency: 'cad',
      description: 'Monthly reciprocity contribution',
      error: '',
      creditCardPayment: null
    };
  }

  calculatePayments = async form => {
    try {
      const region = await getRegionFromForm(form);
      const monthlyPayment = Math.ceil(calculateRenterPayment(form.rent, region.id));

      this.setState({
        mode: "calculate",
        registration: form,
        region,
        monthlyPayment,
        address: form.address,
        total: monthlyPayment,
        pledgeOnly: isPledgeOnlyRegion(region)
      });
    } catch (error) {
      this.setState({ mode: 'error', error: error.message });
    }
  }

  handlePublicMapChange = () => {
    this.setState({ publicMap: !this.state.publicMap });
  }

  handleCreditCardSubmit = async creditCardPayment => {
    this.setState({ creditCardPayment }, async () => {
      await handlePaymentSubmission({
        state: this.state,
        creditCardPayment,
        onSuccess: () => this.setState({ mode: "success" }),
        onError: (error) => this.setState({ mode: "error", error }),
        pageType: 'renter'
      });
    });
  }

  subtext() {
    return (
      <>
        <p>
          Reciprocity payments offer a simple, accessible way for homes, businesses, and
          events to act on the Indigenous principles that shaped and were shaped by the lands and
          waters we love and rely on. They are voluntary, ongoing contributions that move beyond
          symbolic acknowledgment toward meaningful action and meaningful relationships.
        </p>
        <p>
          Type in your rental address to see your recommended monthly Reciprocity Payment. It's about 1% of your monthly rent, or 12% annually.
        </p>
      </>
    )
  }

  render() {
    const { mode, region, monthlyPayment, address, publicMap, total } = { ...this.state };

    return (
      <CalculatorLayout title="'m a renter" highlightChar="i" hideTitle={mode === 'success'}>
        {mode === 'register' &&
          <Registration type={RegistrationTypes.renter} image={'/assets/reciprocity-renter-logo.png'} submitText="Calculate my reciprocity payment"
            subText={this.subtext()}
            buttonColor="#9F3A5A"
            onSuccess={this.calculatePayments}
          />
        }
        {mode === 'success' &&
          <Success type='renter' pledgeOnly={this.state.pledgeOnly} />
        }
        {mode === 'error' &&
          <Alert variant='danger'>{this.state.error}</Alert>
        }
        {mode === 'calculate' &&
          <Container className='mb-5'>
            <Row>
              <RegionSummary region={region} />
            </Row>
            <Row>
              {isPaymentRegion(region.id) ?
                <p className='mb-3' style={{ fontWeight: 'bold' }}>
                  We've calculated your recommended Reciprocity Payment for {currentYear} at a rate of 1% of your monthly rent.
                </p>
                :
                <p className='mb-3' style={{ fontWeight: 'bold' }}>
                  We've calculated your recommended Reciprocity Payment but we're not set up to take payments in your area yet. Sign up below and we'll get in touch once we're ready to go.
                </p>
              }
            </Row>
            <Row>
              <Col xs={0} sm={1} lg={3}></Col>
              <Col xs={12} sm={10} lg={6}>
                <Row className={styles.buttonRow}>
                  <Col>
                    <PaymentSelectButton isSelected type="monthly" address={address} paymentValue={monthlyPayment} year={currentYear}
                      setActivePaymentType={() => { }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Card className={styles.totalCard}>
                    <Card.Header>Total Payments Today:</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col xs={8}>
                          Subscription Payment<br />
                          Starting today, {today.format('MMM. D, yyyy')}<br />
                          Billed each month on the {today.format('Do')}
                        </Col>
                        <Col xs={4} className='text-right'>
                          <CurrencyText value={monthlyPayment} />/month
                        </Col>
                      </Row>
                      <hr className={`${styles.hr} my-1`} />
                      <Row className={styles.totalRow}>
                        <Col xs={8}>
                          Total
                        </Col>
                        <Col xs={4} className='text-right'>
                          <CurrencyText value={total} />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Row>
                <Form.Check className={styles.publicMap} type='checkbox' id='publicMap' checked={publicMap}
                  label={
                    <div>
                      <span>I'd like to be recognized on the </span>
                      <Link to={'/reciprocity-map'} target='_blank'>Reciprocity Map.</Link>
                      <span> Please note: your name or address will never be shown on the map.</span>
                    </div>
                  }
                  onChange={this.handlePublicMapChange}
                />
                <div className='d-flex justify-content-center'>
                  <WhatsNextPopover />
                </div>
                <CreditCardForm className={styles.creditCardForm} order={this.state} submit={this.handleCreditCardSubmit} />
                <div className='text-justify mt-4 mb-3'>
                  <small><i>Reciprocity respects your privacy. By clicking {this.state.pledgeOnly ? "'PLEDGE NOW'" : "'MAKE MY RECIPROCITY PAYMENT'"} you consent to share your information with Reciprocity and receive updates, including when we are up and running in your area, by email, text and/or phone. You can unsubscribe at any time.</i></small>
                </div>
                <Contact type='problem' className='text-center pt-2' />
              </Col>
              <Col xs={0} sm={1} lg={3}></Col>
            </Row>
          </Container>
        }
      </CalculatorLayout>
    );
  }
}

export default ImARenter;
